import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('huacaya_cookie_consent');
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else if (consent === 'accepted') {
      // Load analytics if already accepted
      loadAnalytics();
    }
  }, []);

  const loadAnalytics = () => {
    // Google Analytics
    if (window.gtag && !window.analyticsLoaded) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
      window.analyticsLoaded = true;
    }
  };

  const handleAccept = () => {
    localStorage.setItem('huacaya_cookie_consent', 'accepted');
    localStorage.setItem('huacaya_cookie_date', new Date().toISOString());
    loadAnalytics();
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem('huacaya_cookie_consent', 'declined');
    localStorage.setItem('huacaya_cookie_date', new Date().toISOString());
    // Disable analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
    }
    setShowBanner(false);
  };

  const t = {
    it: {
      message: 'Utilizziamo cookie per migliorare la tua esperienza.',
      accept: 'Accetta',
      decline: 'Rifiuta',
      privacy: 'Privacy'
    },
    en: {
      message: 'We use cookies to improve your experience.',
      accept: 'Accept',
      decline: 'Decline',
      privacy: 'Privacy'
    }
  };

  const text = t[language] || t.it;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50"
        >
          <div className="bg-[#1C1C1E]/95 backdrop-blur-lg border border-[#3A3A3C] rounded-2xl p-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-[#F5F5F7] text-sm leading-relaxed">
                  {text.message}{' '}
                  <Link 
                    to="/privacy" 
                    className="text-[#C9B896] hover:underline"
                  >
                    {text.privacy}
                  </Link>
                </p>
              </div>
              <button
                onClick={handleDecline}
                className="text-[#86868B] hover:text-white transition-colors p-1"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleDecline}
                className="flex-1 px-4 py-2 text-sm font-medium text-[#86868B] hover:text-white transition-colors rounded-lg border border-[#3A3A3C] hover:border-[#5A5A5C]"
              >
                {text.decline}
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#C9B896] hover:bg-[#A89770] transition-colors rounded-lg"
              >
                {text.accept}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
